import { z } from "zod";
import { AutosaveTriggerName } from "./autosave.js";
import { Hex32, SessionId, U64String } from "./ids.js";

export const SessionStartRequest = z.object({
  coreBuildHash: Hex32,
  romSha1: z.string().regex(/^[0-9a-f]{40}$/),
  clientVersion: z.string().max(64),
});
export type SessionStartRequest = z.infer<typeof SessionStartRequest>;

export const ComposeNotice = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("ownership_changed"), asset: z.string(), nickname: z.string(), direction: z.enum(["in", "out"]) }),
  z.object({ kind: z.literal("overflow"), count: z.number().int() }),
  z.object({ kind: z.literal("items_waiting"), index: z.number().int(), amount: z.number().int() }),
  z.object({ kind: z.literal("glitch_money_dropped"), amount: z.number().int() }),
  z.object({ kind: z.literal("purse_capped"), amount: z.number().int() }),
]);
export type ComposeNotice = z.infer<typeof ComposeNotice>;

/**
 * §3.5: the ROM patch a session runs. The client and the replay verifier apply
 * `applyRomPatch(rom, dayCarePriceTable(rcdPerSol), game, version)`; `null` runs the retail ROM unchanged.
 */
export const RomPatch = z.object({ version: z.number().int().positive(), rcdPerSol: U64String });
export type RomPatch = z.infer<typeof RomPatch>;

export const SessionStartResponse = z.object({
  sessionId: SessionId,
  romPatch: RomPatch.nullable(),
  sram: z.string(), // base64 of zstd-compressed 32 KiB SRAM
  sramSha256: Hex32,
  serverNonce: Hex32,
  gameVersion: z.enum(["RED", "BLUE"]),
  autosaveTriggers: z.array(AutosaveTriggerName),
  overflow: z.array(z.object({ asset: z.string(), species: z.number().int(), level: z.number().int(), nickname: z.string() })),
  notices: z.array(ComposeNotice),
  /** save_seq of the canonical save the SRAM was composed from ("0" when there is none). */
  baseSaveSeq: U64String.optional(),
});
export type SessionStartResponse = z.infer<typeof SessionStartResponse>;

/** §7.5: an offline session older than this (by its client start time) is never adopted. */
export const OFFLINE_SESSION_MAX_AGE_DAYS = 7;

/**
 * POST /sessions/adopt (§7.5): turns a session the client started offline into a server session.
 * `sessionId` and `nonce` are the ones the client wrote into its PKIL session header; the base is
 * the canonical SRAM it booted from.
 */
export const SessionAdoptRequest = z.object({
  sessionId: z.string().uuid(),
  nonce: Hex32,
  startedAt: z.string().datetime(),
  baseSramSha256: Hex32,
  /** save_seq of the base when the client knows it; the API locates the base by its SHA-256 either way. */
  baseSaveSeq: U64String.nullable(),
  coreBuildHash: Hex32,
  romSha1: z.string().regex(/^[0-9a-f]{40}$/),
  clientVersion: z.string().max(64),
  romPatch: RomPatch,
});
export type SessionAdoptRequest = z.infer<typeof SessionAdoptRequest>;

/**
 * - `base_changed`: a save committed since the base (or a new game started, or the base is unknown)
 * - `session_open_elsewhere`: a session started after this one is still live on another device
 * - `too_old`: started more than OFFLINE_SESSION_MAX_AGE_DAYS ago
 * - `rules_changed`: core build, ROM, version lock or ROM patch is no longer accepted
 * - `session_id_taken`: the id belongs to a different session
 */
export const OfflineRejectReason = z.enum(["base_changed", "session_open_elsewhere", "too_old", "rules_changed", "session_id_taken"]);
export type OfflineRejectReason = z.infer<typeof OfflineRejectReason>;

export const SessionAdoptResponse = z.discriminatedUnion("status", [
  z.object({ status: z.literal("adopted"), sessionId: SessionId }),
  z.object({ status: z.literal("rejected"), reason: OfflineRejectReason }),
]);
export type SessionAdoptResponse = z.infer<typeof SessionAdoptResponse>;

export const CheckpointKind = z.enum(["game", "autosave"]);
export type CheckpointKind = z.infer<typeof CheckpointKind>;

/** `meta` part of POST /sessions/:id/checkpoints (§20.3). */
export const CheckpointMeta = z
  .object({
    seqInSession: z.number().int().nonnegative(),
    frame: U64String,
    sramSha256: Hex32,
    lastChunkSeq: z.number().int().nonnegative(),
    kind: CheckpointKind,
    triggers: z.array(AutosaveTriggerName).optional(),
  })
  .refine((m) => (m.kind === "autosave") === (m.triggers !== undefined && m.triggers.length > 0), {
    message: "triggers are required for autosave checkpoints and forbidden otherwise",
  });
export type CheckpointMeta = z.infer<typeof CheckpointMeta>;

/** Headers on POST /sessions/:id/inputs. */
export const CHUNK_SEQ_HEADER = "x-chunk-seq";
export const CHAIN_HASH_HEADER = "x-chain-hash";
export const PKIL_CONTENT_TYPE = "application/x-pkil";
/** Chunks are uploaded zstd-compressed; the API decompresses before decoding. */
export const PKIL_ENCODING_HEADER = "x-pkil-encoding";
export const PKIL_ENCODING_ZSTD = "zstd";
